import { ActionSectionItem, ActionSectionItemAction } from '@wix/add-panel-common';
import { symbol as Symbol } from '@wix/santa-editor-symbols';
import classnames from 'classnames';
import React, { FC } from 'react';
import { useActionsDispatcher } from '../../../../../ActionsDispatcher';
import { useCurrentCategory } from '../../../../../CurrentCategoryProvider';
import { useGetFallbackImage } from '../../../../../hooks/useGetFallbackImage';
import { useUtils } from '../../../../../UtilsProvider';
import { ActionItem as BaseActionItem } from '../../components/ActionItem';
import { useItemData } from '../../hooks/useItemData';
import styles from './ActionItem.scss';
import type { ActionItemProps } from './ActionItem.types';

const DEFAULT_ACTION_ICON = 'arrowWithStates';

export const ActionItem: FC<ActionItemProps> = ({ item: _item, section }) => {
  const {
    sectionName,
    translations: { title: sectionTitle },
  } = section;

  const {
    media: { getMediaUrl },
    translate,
  } = useUtils();

  const item = useItemData<ActionSectionItem>(_item);

  const {
    automationId,
    title,
    desc,
    action,
    image: enabledImage,
    disabledImage,
    symbol = DEFAULT_ACTION_ICON,
    disabled,
  } = item;

  const category = useCurrentCategory();
  const getFallbackImage = useGetFallbackImage();
  const {
    dispatchOpenMediaManager,
    dispatchAddBlankPage,
    dispatchAddQuickActionBar,
    dispatchAddBackToTopButton,
    dispatchAddWelcomeScreen,
    dispatchOpenPhotoStudio,
    dispatchAddChromeThemeColor,
    dispatchInstallAppAndNotify,
    dispatchAddPreset,
    dispatchAddCollection,
    dispatchAddDataSet,
    dispatchAddFormDataSet,
    dispatchOpenAccountSettings,
    dispatchCreateNewForm,
    dispatchAddExistingForm,
    dispatchComponentClick,
  } = useActionsDispatcher();

  const { id: categoryId } = category;

  const handleActionItemClick = () => {
    switch (action) {
      case ActionSectionItemAction.OpenMediaManager: {
        dispatchOpenMediaManager({
          categoryId,
          sectionTitle,
          sectionName,
          biItemName: item.biItemName,
          mediaManagerOptions: item.mediaManagerOptions,
        });
        return;
      }

      case ActionSectionItemAction.AddBlankPage:
        dispatchAddBlankPage({ item });
        break;

      case ActionSectionItemAction.AddQuickActionBar:
        dispatchAddQuickActionBar({ item });
        break;

      case ActionSectionItemAction.AddBackToTopButton:
        dispatchAddBackToTopButton({ item });
        break;

      case ActionSectionItemAction.AddWelcomeScreen:
        dispatchAddWelcomeScreen({ item });
        break;

      case ActionSectionItemAction.OpenPhotoStudio:
        dispatchOpenPhotoStudio({ item });
        break;

      case ActionSectionItemAction.AddChromeThemeColor:
        dispatchAddChromeThemeColor({ item });
        break;

      case ActionSectionItemAction.InstallAppAndNotify:
        dispatchInstallAppAndNotify({ item });
        break;

      case ActionSectionItemAction.AddPreset:
        dispatchAddPreset({ item });
        break;

      case ActionSectionItemAction.AddCollection:
        dispatchAddCollection();
        break;

      case ActionSectionItemAction.AddDataSet:
        dispatchAddDataSet({ item });
        break;

      case ActionSectionItemAction.AddFormDataSet:
        dispatchAddFormDataSet({ item });
        break;

      case ActionSectionItemAction.OpenAccountSettings:
        dispatchOpenAccountSettings({
          path: item.accountSettingsPath,
          itemId: item.id,
          sectionTitle,
          categoryId,
        });
        break;

      case ActionSectionItemAction.CreateNewForm:
        dispatchCreateNewForm();
        break;

      case ActionSectionItemAction.AddExistingForm:
        dispatchAddExistingForm();
        break;

      default:
        dispatchComponentClick({ item, compDef: item.structure, category, itemId: item.id, section });
    }
  };

  return (
    <BaseActionItem
      disabled={disabled}
      dataHook={automationId}
      title={translate(title)}
      description={translate(desc)}
      illustration={
        <img src={getMediaUrl(enabledImage, getFallbackImage(enabledImage))} alt="" className={styles.illustration} />
      }
      disabledIllustration={
        <img src={getMediaUrl(disabledImage, getFallbackImage(disabledImage))} alt="" className={styles.illustration} />
      }
      actionIcon={<Symbol name={symbol} className={classnames({ disabled })} />}
      tooltipContent={translate(item.tooltip)}
      onClick={handleActionItemClick}
      shouldTranslate={false}
    />
  );
};
